import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { ViewMode } from "@/admin/dashboard/types";
import type { DateYYYYMMDD } from "@/types/date";

export interface InitialDateState {
    value: DateYYYYMMDD | null;
    highlightedBookingId: string | null;
    /** Increments on every navigateToBooking call so consumers can detect
     *  a new navigation even when the date and bookingId haven't changed. */
    navigationSequence: number;
    /** Set by navigateToBooking when the target view can only be shown in one
     *  view mode (e.g. cancelled bookings aren't shown in grid view). Read
     *  directly in the same render that remounts DataFetcherView, since that
     *  remount would otherwise race a side-effect-based approach. Cleared once
     *  consumed. */
    forcedViewMode: ViewMode | null;
    /** Set by navigateToBooking when the target can't be shown in floor view
     *  (floor always draws "right now", never the booking's day). Unlike
     *  forcedViewMode this only applies when the admin is currently on floor, so
     *  list/grid users keep their view. Consumed and cleared together with
     *  forcedViewMode. */
    leaveFloorView: boolean;
}

const initialState: InitialDateState = {
    value: null,
    highlightedBookingId: null,
    navigationSequence: 0,
    forcedViewMode: null,
    leaveFloorView: false,
};

const initialDateSlice = createSlice({
    name: "initialDate",
    initialState,
    reducers: {
        setInitialDate: (state, action: PayloadAction<DateYYYYMMDD>) => {
            state.value = action.payload;
            state.highlightedBookingId = null;
        },
        navigateToBooking: (
            state,
            action: PayloadAction<{
                date: DateYYYYMMDD;
                bookingId: string;
                forceViewMode?: ViewMode;
                leaveFloorView?: boolean;
            }>,
        ) => {
            state.value = action.payload.date;
            state.highlightedBookingId = action.payload.bookingId;
            state.navigationSequence += 1;
            state.forcedViewMode = action.payload.forceViewMode ?? null;
            state.leaveFloorView = action.payload.leaveFloorView ?? false;
        },
        clearHighlightedBooking: (state) => {
            state.highlightedBookingId = null;
        },
        clearForcedViewMode: (state) => {
            state.forcedViewMode = null;
            state.leaveFloorView = false;
        },
    },
});

export const { setInitialDate, navigateToBooking, clearHighlightedBooking, clearForcedViewMode } =
    initialDateSlice.actions;
export default initialDateSlice.reducer;
